/**
 * Product Carousel
 * Feature: 019-product-page-carousel - US2
 *
 * Provides interactive image carousel with:
 * - Thumbnail navigation (event delegation)
 * - Arrow button navigation
 * - Keyboard support (Arrow keys, Enter/Space)
 * - Touch/swipe gestures (Pointer Events API)
 * - Smooth transitions (<300ms)
 * - Accessibility (ARIA labels, focus management)
 * - Reduced motion support
 */

class ProductCarousel {
  constructor(container) {
    if (!container) return;

    this.container = container;
    this.mainImage = container.querySelector('[data-main-image]');
    this.thumbnailContainer = container.querySelector('[data-thumbnail-container]');
    this.thumbnails = container.querySelectorAll('[data-thumbnail]');
    this.prevButton = container.querySelector('[data-carousel-prev]');
    this.nextButton = container.querySelector('[data-carousel-next]');

    this.currentIndex = 0;
    this.mediaData = this.loadMediaData();
    this.isTransitioning = false;

    // Check for reduced motion preference
    this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  /**
   * Load media data from JSON script tag
   */
  loadMediaData() {
    const dataElement = this.container.querySelector('[data-product-media-json]');
    if (dataElement) {
      try {
        return JSON.parse(dataElement.textContent);
      } catch (e) {
        console.error('Failed to parse product media data:', e);
      }
    }
    return { media: [] };
  }

  /**
   * Initialize carousel (T016)
   */
  init() {
    if (this.thumbnails.length === 0) {
      return;
    }

    // Hide controls if only one image (T020)
    if (this.thumbnails.length === 1) {
      this.hideControls();
      return;
    }

    // Event delegation for thumbnails (T016)
    if (this.thumbnailContainer) {
      this.thumbnailContainer.addEventListener('click', this.handleThumbnailClick.bind(this));
    }

    // Arrow navigation (T016)
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.navigate(-1));
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.navigate(1));
    }

    // Keyboard navigation (T017)
    this.setupKeyboardNavigation();

    // Touch/swipe support (T019)
    this.setupTouchSupport();
  }

  /**
   * Handle thumbnail click via event delegation (T016)
   */
  handleThumbnailClick(event) {
    const thumbnail = event.target.closest('[data-thumbnail]');
    if (!thumbnail) return;

    const index = parseInt(thumbnail.dataset.thumbnail, 10);
    if (isNaN(index)) return;

    this.navigateToIndex(index);
  }

  /**
   * Navigate by direction (T016)
   * @param {number} direction - -1 for previous, 1 for next
   */
  navigate(direction) {
    if (this.isTransitioning) return;

    const newIndex = this.currentIndex + direction;

    // Loop navigation (T016)
    let targetIndex;
    if (newIndex < 0) {
      targetIndex = this.thumbnails.length - 1;
    } else if (newIndex >= this.thumbnails.length) {
      targetIndex = 0;
    } else {
      targetIndex = newIndex;
    }

    this.navigateToIndex(targetIndex);
  }

  /**
   * Navigate to specific index (T018 - with smooth transitions)
   */
  navigateToIndex(index) {
    if (index === this.currentIndex || this.isTransitioning) return;
    if (index < 0 || index >= this.thumbnails.length) return;
    this.isTransitioning = true;

    // Update main image with transition (T018)
    this.updateMainImage(index);

    // Update thumbnail active state
    this.updateThumbnailStates(index);

    // Scroll thumbnail into view
    this.scrollThumbnailIntoView(index);

    // Update current index
    this.currentIndex = index;

    // Reset transition lock after animation
    const transitionDuration = this.prefersReducedMotion ? 0 : 300;
    setTimeout(() => {
      this.isTransitioning = false;
    }, transitionDuration);
  }

  /**
   * Update main image with fade transition (T018)
   */
  updateMainImage(index) {
    if (!this.mainImage) return;

    const media = this.mediaData.media[index];
    if (!media) return;

    // Fade out current image (unless reduced motion)
    if (!this.prefersReducedMotion) {
      this.mainImage.style.opacity = '0';
    }

    // Update image after fade
    const updateDelay = this.prefersReducedMotion ? 0 : 150;
    setTimeout(() => {
      // Update src and srcset
      if (media.media_type === 'image') {
        this.mainImage.src = media.src;

        // Update srcset for responsive images
        this.mainImage.srcset = `${media.src.replace('width=1000', 'width=800')} 800w, ${media.src.replace('width=1000', 'width=1200')} 1200w, ${media.src.replace('width=1000', 'width=1600')} 1600w`;

        if (media.srcLarge) {
          // Update for lightbox
          this.mainImage.dataset.srcLarge = media.srcLarge;
        }
        this.mainImage.alt = media.alt || '';
      }

      // Fade in new image
      if (!this.prefersReducedMotion) {
        this.mainImage.style.opacity = '1';
      }
    }, updateDelay);
  }

  /**
   * Update thumbnail active states
   */
  updateThumbnailStates(activeIndex) {
    this.thumbnails.forEach((thumbnail, index) => {
      const isActive = index === activeIndex;

      if (isActive) {
        thumbnail.classList.add('product-media__thumbnail--active');
        thumbnail.setAttribute('aria-current', 'true');
      } else {
        thumbnail.classList.remove('product-media__thumbnail--active');
        thumbnail.removeAttribute('aria-current');
      }
    });
  }

  /**
   * Scroll thumbnail into view
   */
  scrollThumbnailIntoView(index) {
    if (!this.thumbnailContainer) return;

    const thumbnail = this.thumbnails[index];
    if (!thumbnail) return;

    // Smooth scroll to thumbnail
    const scrollBehavior = this.prefersReducedMotion ? 'auto' : 'smooth';
    thumbnail.scrollIntoView({
      behavior: scrollBehavior,
      block: 'nearest',
      inline: 'center'
    });
  }

  /**
   * Setup keyboard navigation (T017)
   */
  setupKeyboardNavigation() {
    // Arrow key navigation on main carousel
    document.addEventListener('keydown', (event) => {
      // Only respond if carousel is focused or active
      if (!this.container.contains(document.activeElement)) return;

      switch(event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          this.navigate(-1);
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.navigate(1);
          break;
      }
    });

    // Enter/Space on thumbnails
    this.thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.navigateToIndex(index);
        }
      });
    });
  }

  /**
   * Setup touch/swipe support (T019)
   * Uses Pointer Events API for unified mouse/touch handling
   */
  setupTouchSupport() {
    if (!this.mainImage) return;

    let startX = 0;
    let startY = 0;
    let isDragging = false;

    const mainWrapper = this.mainImage.closest('.product-media__main-wrapper');
    if (!mainWrapper) return;

    // Use Pointer Events API (modern, unified approach)
    mainWrapper.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
      startY = event.clientY;
      isDragging = true;
      mainWrapper.style.cursor = 'grabbing';
    });

    mainWrapper.addEventListener('pointermove', (event) => {
      if (!isDragging) return;

      // Prevent default to stop scrolling while swiping
      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      // Only prevent if horizontal swipe (not vertical scroll)
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        event.preventDefault();
      }
    });

    mainWrapper.addEventListener('pointerup', (event) => {
      if (!isDragging) return;

      const deltaX = event.clientX - startX;
      const deltaY = event.clientY - startY;

      // Determine if this was a swipe (not just a click)
      const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
      const swipeThreshold = 50; // pixels

      if (isHorizontalSwipe && Math.abs(deltaX) > swipeThreshold) {
        if (deltaX > 0) {
          // Swipe right - go to previous
          this.navigate(-1);
        } else {
          // Swipe left - go to next
          this.navigate(1);
        }
      }

      isDragging = false;
      mainWrapper.style.cursor = '';
    });

    mainWrapper.addEventListener('pointercancel', () => {
      isDragging = false;
      mainWrapper.style.cursor = '';
    });

    // CSS touch-action for better mobile behavior
    mainWrapper.style.touchAction = 'pan-y pinch-zoom';
  }

  /**
   * Hide controls when only one image (T020)
   */
  hideControls() {
    if (this.prevButton) this.prevButton.style.display = 'none';
    if (this.nextButton) this.nextButton.style.display = 'none';
    if (this.thumbnailContainer) {
      const wrapper = this.thumbnailContainer.closest('.product-media__thumbnails-wrapper');
      if (wrapper) wrapper.style.display = 'none';
    }

    // Add data attribute for CSS targeting
    this.container.dataset.singleImage = 'true';
  }

  /**
   * Public method to navigate to specific media by ID
   * Used by variant selector to switch to variant image
   */
  navigateToMediaId(mediaId) {
    const index = this.mediaData.media.findIndex(m => m.id === mediaId);
    if (index !== -1) {
      this.navigateToIndex(index);
    }
  }
}

/**
 * Initialize carousel on page load
 */
document.addEventListener('DOMContentLoaded', function() {
  const carouselContainer = document.querySelector('[data-product-media]');

  if (carouselContainer) {
    // Initialize carousel
    const carousel = new ProductCarousel(carouselContainer);

    // Make carousel accessible globally for variant selector integration
    window.productCarousel = carousel;
  }
});

/**
 * Export for module systems (if needed)
 */
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductCarousel;
}
